import axiosInstance from './axiosInstance';
import { IPdfTemplate } from '../types/pdfTemplateTypes';

const API_BASE = '/api/pdf-templates';

export async function getPdfTemplateServiceErrorMessage(error: any, fallback: string): Promise<string> {
  const data = error?.response?.data;

  if (data instanceof Blob) {
    const text = await data.text();
    try {
      const parsed = JSON.parse(text);
      return parsed?.message || fallback;
    } catch {
      return text || fallback;
    }
  }

  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      return parsed?.message || data || fallback;
    } catch {
      return data || fallback;
    }
  }

  return data?.message || error?.message || fallback;
}

/**
 * Get all PDF templates
 */
export async function getPdfTemplates(): Promise<IPdfTemplate[]> {
  const { data } = await axiosInstance.get(API_BASE);
  return data;
}

/**
 * Get single PDF template by ID
 */
export async function getPdfTemplate(id: string): Promise<IPdfTemplate> {
  const { data } = await axiosInstance.get(`${API_BASE}/${id}`);
  return data;
}

/**
 * Get active PDF templates for dropdown menus
 */
export async function getActivePdfTemplates(type?: 'products' | 'wine'): Promise<IPdfTemplate[]> {
  const params = type ? { type } : {};
  const { data } = await axiosInstance.get(`${API_BASE}/active/list`, { params });
  return data;
}

/**
 * Create new PDF template
 */
export async function createPdfTemplate(template: Omit<IPdfTemplate, '_id'>): Promise<IPdfTemplate> {
  const { data } = await axiosInstance.post(API_BASE, template);
  return data;
}

/**
 * Update PDF template
 */
export async function updatePdfTemplate(id: string, template: Partial<IPdfTemplate>): Promise<IPdfTemplate> {
  const { data } = await axiosInstance.patch(`${API_BASE}/${id}`, template);
  return data;
}

/**
 * Delete PDF template
 */
export async function deletePdfTemplate(id: string): Promise<void> {
  await axiosInstance.delete(`${API_BASE}/${id}`);
}

/**
 * Render PDF and download
 */
export async function downloadPdf(templateId: string, assetId: string): Promise<void> {
  const response = await axiosInstance.post(
    `${API_BASE}/render`,
    { templateId, assetId },
    { responseType: 'blob' }
  );
  
  // Extract filename from Content-Disposition header
  const contentDisposition = response.headers['content-disposition'] || '';
  const filenameMatch = contentDisposition.match(/filename="?([^";\s]+)"?/i);
  const filename = filenameMatch 
    ? decodeURIComponent(filenameMatch[1]) 
    : 'document.pdf';
  
  // Create download link
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Render multi-product PDF and download as a single file
 */
export async function downloadMultiPdf(templateId: string, assetIds: string[]): Promise<void> {
  const response = await axiosInstance.post(
    `${API_BASE}/render/multi`,
    { templateId, assetIds },
    { responseType: 'blob' }
  );

  // Extract filename from Content-Disposition header
  const contentDisposition = response.headers['content-disposition'] || '';
  const filenameMatch = contentDisposition.match(/filename="?([^";\s]+)"?/i);
  const filename = filenameMatch
    ? decodeURIComponent(filenameMatch[1])
    : 'multi-document.pdf';

  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Get HTML preview URL (for iframe)
 */
export async function getHtmlPreview(
  templateOrId: IPdfTemplate | string, 
  assetId: string
): Promise<string> {
  const body = typeof templateOrId === 'string' 
    ? { templateId: templateOrId, assetId }
    : { template: templateOrId, assetId };
  
  const response = await axiosInstance.post(
    `${API_BASE}/render/preview`,
    body,
    { responseType: 'text' }
  );
  
  // Return as data URL for iframe
  return `data:text/html;charset=utf-8,${encodeURIComponent(response.data)}`;
}

/**
 * Get PDF preview as blob URL (for iframe/embed)
 */
export async function getPdfPreviewUrl(
  templateOrId: IPdfTemplate | string, 
  assetId: string
): Promise<string> {
  const body = typeof templateOrId === 'string' 
    ? { templateId: templateOrId, assetId }
    : { template: templateOrId, assetId };
  
  const response = await axiosInstance.post(
    `${API_BASE}/render/pdf-preview`,
    body,
    { responseType: 'blob' }
  );
  
  const blob = new Blob([response.data], { type: 'application/pdf' });
  return window.URL.createObjectURL(blob);
}

export async function getPdfTemplateThumbnailUrl(
  templateOrId: IPdfTemplate | string,
  assetId?: string,
  width = 360
): Promise<string> {
  const body = typeof templateOrId === 'string'
    ? { templateId: templateOrId, assetId, width }
    : { template: templateOrId, assetId, width };

  const response = await axiosInstance.post(
    `${API_BASE}/render/thumbnail`,
    body,
    { responseType: 'blob' }
  );

  const blob = new Blob([response.data], { type: 'image/png' });
  return window.URL.createObjectURL(blob);
}

/**
 * Get multi-product PDF preview as blob URL (for iframe/embed)
 */
export async function getMultiPdfPreviewUrl(
  templateOrId: IPdfTemplate | string,
  assetIds: string[]
): Promise<string> {
  const body = typeof templateOrId === 'string'
    ? { templateId: templateOrId, assetIds }
    : { template: templateOrId, assetIds };

  const response = await axiosInstance.post(
    `${API_BASE}/render/multi/preview`,
    body,
    { responseType: 'blob' }
  );

  const blob = new Blob([response.data], { type: 'application/pdf' });
  return window.URL.createObjectURL(blob);
}

export async function getMultiPdfTemplateThumbnailUrl(
  templateOrId: IPdfTemplate | string,
  assetIds: string[],
  width = 360
): Promise<string> {
  const body = typeof templateOrId === 'string'
    ? { templateId: templateOrId, assetIds, width }
    : { template: templateOrId, assetIds, width };

  const response = await axiosInstance.post(
    `${API_BASE}/render/multi/thumbnail`,
    body,
    { responseType: 'blob' }
  );

  const blob = new Blob([response.data], { type: 'image/png' });
  return window.URL.createObjectURL(blob);
}
